import React, { useEffect, useState } from "react";
import {
  FaSyncAlt,
  FaFileInvoice,
  FaReceipt,
  FaBox,
  FaMoneyBillWave,
  FaMoneyCheckAlt,
  FaChartPie,
  FaList,
  FaRegListAlt,
  FaStore
} from "react-icons/fa";
import { motion } from "framer-motion";
import axios from "axios";
import html2pdf from "html2pdf.js";

const MonthlyReport = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({});
  const [outlets, setOutlets] = useState([]);

  const [filters, setFilters] = useState({
    outlet_id: "",
    start_year_month: "",
    end_year_month: "",
    page: 1,
    per_page: 5,
  });

  const role = (localStorage.getItem("role") || "").toLowerCase();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const storedOutletId = localStorage.getItem("id_outlet");

  useEffect(() => {
    if (role === "manager") {
      setFilters((prev) => ({ ...prev, outlet_id: storedOutletId }));
    } else if (["owner", "admin", "superadmin"].includes(role)) {
      fetchOutlets();
    }
  }, [role, storedOutletId]);

  useEffect(() => {
    if (filters.outlet_id || ["owner", "admin", "superadmin"].includes(role)) fetchReports();
  }, [filters]);

  const fetchOutlets = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/v1/outlet`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOutlets(res.data.data || []);
    } catch (err) {
      console.error("Error fetching outlets", err);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        ...filters,
        page: filters.page.toString(),
        per_page: filters.per_page.toString(),
      }).toString();

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/report/monthly-report?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setReports(res.data.data || []);
      setMeta(res.data.meta || {});
    } catch (err) {
      console.error("Error fetching reports", err);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: name === "per_page" || name === "page" ? parseInt(value, 10) : value,
      ...(name !== "page" && { page: 1 }),
    }));
  };

  const formatMonth = (year, month) => {
    if (!year || !month) return "-";
    const date = new Date(year, month - 1);
    return date.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById("printable-report");
    if (!element) return;

    const opt = {
      margin: 0.4,
      filename: `monthly-report-${filters.start_year_month || "all"}_to_${filters.end_year_month || "all"}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
    };

    html2pdf()
      .from(element)
      .set(opt)
      .save();
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Title & Tabs Bar */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-50 rounded-2xl border border-emerald-100 text-emerald-600">
            <FaRegListAlt className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Laporan Bulanan (Monthly Report)
            </h1>
            <p className="text-xs text-gray-500">
              Rekapitulasi penjualan, invoice, PO, serta omset pemasukan dan pengeluaran
            </p>
          </div>
        </div>

        {/* Tab & Download Button */}
        <div className="flex items-center gap-3 w-full md:w-auto justify-end">
          <div className="p-1 bg-gray-100 rounded-2xl flex items-center gap-1 text-xs">
            <button
              className={`px-4 py-2 rounded-xl font-bold transition-all flex items-center gap-1.5 ${
                activeTab === "overview"
                  ? "bg-white text-emerald-700 shadow-sm font-extrabold"
                  : "text-gray-500 hover:text-gray-900"
              }`}
              onClick={() => setActiveTab("overview")}
            >
              <FaChartPie className="w-3.5 h-3.5" />
              <span>Overview</span>
            </button>
            <button
              className={`px-4 py-2 rounded-xl font-bold transition-all flex items-center gap-1.5 ${
                activeTab === "list"
                  ? "bg-white text-emerald-700 shadow-sm font-extrabold"
                  : "text-gray-500 hover:text-gray-900"
              }`}
              onClick={() => setActiveTab("list")}
            >
              <FaList className="w-3.5 h-3.5" />
              <span>Tabel Ringkasan</span>
            </button>
          </div>

          {activeTab === "list" && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleDownloadPDF}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-2xl shadow-lg shadow-emerald-600/20 transition-all"
            >
              <FaFileInvoice className="w-3.5 h-3.5" />
              <span>Download PDF</span>
            </motion.button>
          )}
        </div>
      </motion.div>

      {/* Filter Bar */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white p-5 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 flex flex-wrap gap-4 items-center justify-between text-xs"
      >
        <div className="flex flex-wrap gap-3 items-center w-full md:w-auto">
          {["owner", "admin", "superadmin"].includes(role) && (
            <select
              name="outlet_id"
              value={filters.outlet_id}
              onChange={handleChange}
              className="p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            >
              <option value="">Semua Outlet</option>
              {outlets.map((outlet) => (
                <option key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </option>
              ))}
            </select>
          )}

          <div className="flex items-center gap-2">
            <span className="text-gray-400 font-bold uppercase text-[10px]">Mulai:</span>
            <input
              type="month"
              name="start_year_month"
              value={filters.start_year_month}
              onChange={handleChange}
              className="p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-gray-400 font-bold uppercase text-[10px]">Sampai:</span>
            <input
              type="month"
              name="end_year_month"
              value={filters.end_year_month}
              onChange={handleChange}
              className="p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <select
            name="per_page"
            value={filters.per_page}
            onChange={handleChange}
            className="p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
          >
            <option value={5}>5 Halaman</option>
            <option value={10}>10 Halaman</option>
            <option value={20}>20 Halaman</option>
          </select>

          <button
            onClick={fetchReports}
            className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-2xl transition-colors flex items-center gap-1.5"
          >
            <FaSyncAlt className="w-3.5 h-3.5 text-emerald-600" />
            <span>Refresh</span>
          </button>
        </div>
      </motion.div>

      {/* Content Area */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : reports.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center text-gray-400 font-semibold border border-gray-100">
          Belum ada data laporan bulanan ditemukan.
        </div>
      ) : (
        <div className="space-y-6">
          {/* OVERVIEW TAB */}
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {reports.map((report, index) => (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  key={index}
                  className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-4 hover:border-emerald-200 transition-colors"
                >
                  <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                    <h2 className="text-lg font-extrabold text-gray-900 capitalize">
                      {formatMonth(report.year, report.month)}
                    </h2>
                    <span className="px-3 py-1 bg-emerald-50 text-emerald-700 font-mono text-xs font-bold rounded-full border border-emerald-100">
                      Tahun {report.year}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div className="p-3 bg-gray-50 rounded-2xl text-center space-y-1">
                      <FaReceipt className="w-4 h-4 text-emerald-600 mx-auto" />
                      <span className="text-[10px] text-gray-400 font-bold uppercase">Transaksi</span>
                      <p className="font-extrabold text-gray-900 text-sm">{report.total_transaction}</p>
                    </div>

                    <div className="p-3 bg-gray-50 rounded-2xl text-center space-y-1">
                      <FaFileInvoice className="w-4 h-4 text-teal-600 mx-auto" />
                      <span className="text-[10px] text-gray-400 font-bold uppercase">Invoice</span>
                      <p className="font-extrabold text-gray-900 text-sm">{report.total_invoices}</p>
                    </div>

                    <div className="p-3 bg-gray-50 rounded-2xl text-center space-y-1">
                      <FaBox className="w-4 h-4 text-amber-500 mx-auto" />
                      <span className="text-[10px] text-gray-400 font-bold uppercase">PO</span>
                      <p className="font-extrabold text-gray-900 text-sm">{report.total_purchase_order}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-xs pt-2">
                    <div className="p-3.5 bg-emerald-50/60 rounded-2xl border border-emerald-100 space-y-1">
                      <span className="text-[10px] text-emerald-700 font-bold uppercase flex items-center gap-1">
                        <FaMoneyBillWave className="w-3.5 h-3.5" /> Total Pemasukan
                      </span>
                      <p className="font-mono font-extrabold text-emerald-800 text-base">
                        Rp {(report.total_pemasukan || 0).toLocaleString("id-ID")}
                      </p>
                    </div>

                    <div className="p-3.5 bg-rose-50/60 rounded-2xl border border-rose-100 space-y-1">
                      <span className="text-[10px] text-rose-700 font-bold uppercase flex items-center gap-1">
                        <FaMoneyCheckAlt className="w-3.5 h-3.5" /> Total Pengeluaran
                      </span>
                      <p className="font-mono font-extrabold text-rose-800 text-base">
                        Rp {(report.total_pengeluaran || 0).toLocaleString("id-ID")}
                      </p>
                    </div>
                  </div>

                  <div className="p-3 bg-gray-50 rounded-2xl flex justify-between items-center text-xs font-semibold">
                    <span className="text-gray-500">Top Transaksi Nominal:</span>
                    <span className="font-mono font-extrabold text-emerald-700 text-sm">
                      Rp {(report.top_transaction || 0).toLocaleString("id-ID")}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* LIST TAB / PRINT AREA */}
          <div id="printable-report" className={activeTab !== "list" ? "hidden" : ""}>
            <div className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-4">
              <div className="text-center space-y-1 pb-4 border-b border-gray-100">
                <h2 className="text-xl font-extrabold text-gray-900 uppercase tracking-wider">
                  APOTEK DJAYA MEDIKA - LAPORAN REKAP BULANAN
                </h2>
                <p className="text-xs font-bold text-gray-600">
                  {role === "owner"
                    ? outlets.find((o) => o.id.toString() === filters.outlet_id)?.name || "Semua Outlet Cabang"
                    : "Outlet ID: " + storedOutletId}
                </p>
                <p className="text-[11px] text-gray-400 font-mono">
                  Periode: {filters.start_year_month || "Awal"} s/d {filters.end_year_month || "Terbaru"}
                </p>
              </div>

              <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm text-xs">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                      <th className="p-3.5">Periode Bulan</th>
                      <th className="p-3.5 text-center">Transaksi</th>
                      <th className="p-3.5 text-center">Invoice</th>
                      <th className="p-3.5 text-center">PO</th>
                      <th className="p-3.5 text-right">Top Transaksi</th>
                      <th className="p-3.5 text-right">Pemasukan</th>
                      <th className="p-3.5 text-right">Pengeluaran</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reports.map((report, index) => (
                      <tr key={index} className="hover:bg-emerald-50/20 transition-colors">
                        <td className="p-3.5 font-bold text-gray-900">
                          {formatMonth(report.year, report.month)}
                        </td>
                        <td className="p-3.5 text-center font-mono font-bold text-gray-700">
                          {report.total_transaction}
                        </td>
                        <td className="p-3.5 text-center font-mono font-bold text-gray-700">
                          {report.total_invoices}
                        </td>
                        <td className="p-3.5 text-center font-mono font-bold text-gray-700">
                          {report.total_purchase_order}
                        </td>
                        <td className="p-3.5 text-right font-mono font-bold text-gray-800">
                          Rp {(report.top_transaction || 0).toLocaleString("id-ID")}
                        </td>
                        <td className="p-3.5 text-right font-mono font-extrabold text-emerald-700">
                          Rp {(report.total_pemasukan || 0).toLocaleString("id-ID")}
                        </td>
                        <td className="p-3.5 text-right font-mono font-extrabold text-rose-600">
                          Rp {(report.total_pengeluaran || 0).toLocaleString("id-ID")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyReport;
